/**
 * atomNamingBrevity — shared PIPH-319 brevity rules for atom naming.
 *
 * Extracted from `arrowDescriptionTemplate.ts` so the same brevity logic can be
 * reused by other deterministic descriptions (notably the MC-905
 * electron-movement announcement) instead of re-qualifying every atom's feature
 * context. A per-description `BrevityCtx` tracks atom + feature mentions so:
 *   - Rule 1: "oxygen of Oxygen" collapses to "oxygen" (feature qualifier
 *     equals element name).
 *   - Rule 2: the same atom mentioned again drops its charge prefix AND
 *     "of {feature}" suffix.
 *   - Rule 5: when both atoms of a bond share a feature, "of {feature}" is
 *     attached to the joined phrase once rather than to each atom.
 *
 * Casing: the curved-arrow template lowercases element labels ("carbon-3"). A
 * consumer that wants the spec-table capitalization ("Carbon-3" — see MC-905)
 * passes `{ lowercase: false }` to `createBrevityCtx`; everything else (charge
 * stripping, brevity rules) is identical.
 *
 * @package @alchemiesolns/svg-interactive
 * @see PIPH-319, MC-905
 */

import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import {
  getElementName,
  type SerializationResult,
} from '@alchemiesolns/feature-extractor';
import { getAtomNameWithChargeContext } from './atomNaming';

/**
 * Per-description state. Threaded through every name render so atom-level
 * brevity rules (2 and 5) work across the whole sentence.
 */
export interface BrevityCtx {
  workspace: MolecularWorkspace;
  serialized: SerializationResult;
  atomIdToIndex: Map<string, number>;
  /** Atom indices already named in this description. Rule 2. */
  mentioned: Set<number>;
  /** Feature names already named in this description. Rule 2/5 extension. */
  mentionedFeatures: Set<string>;
  /**
   * Lowercase the leading element label ("Carbon-2" → "carbon-2"). Default
   * true (curved-arrow prose). Set false to keep spec-table capitalization.
   */
  lowercase: boolean;
}

export function createBrevityCtx(
  workspace: MolecularWorkspace,
  serialized: SerializationResult,
  atomIdToIndex: Map<string, number>,
  opts: { lowercase?: boolean } = {},
): BrevityCtx {
  return {
    workspace,
    serialized,
    atomIdToIndex,
    mentioned: new Set(),
    mentionedFeatures: new Set(),
    lowercase: opts.lowercase ?? true,
  };
}

/**
 * Look up an atom symbol via the workspace, used when the atom isn't present
 * in `serialized.atoms` (e.g. an explicit hydrogen folded into its parent's
 * hydrogenCount during serialization). Fixes the "Atom-N" leak called out
 * in PIPH-319.
 */
function workspaceSymbolForIndex(ctx: BrevityCtx, atomIndex: number): string | undefined {
  for (const [atomId, idx] of ctx.atomIdToIndex.entries()) {
    if (idx === atomIndex) return ctx.workspace.getAtom(atomId)?.symbol;
  }
  return undefined;
}

/**
 * Split a "{name} of {feature}" string. Returns `[name, featureName]` or
 * `[input, undefined]` when no " of " suffix is present. Uses lastIndexOf
 * so a feature name that itself contains " of " (e.g. "alkene of 5-carbon
 * chain") still matches the outermost split for our purpose: peeling one
 * layer off the right.
 */
function splitFeatureSuffix(name: string): [string, string | undefined] {
  const idx = name.lastIndexOf(' of ');
  if (idx === -1) return [name, undefined];
  return [name.slice(0, idx), name.slice(idx + ' of '.length)];
}

const CHARGE_PREFIX_RE = /^(positively|negatively) charged /;
/**
 * Trailing charge symbol leak (PIPH-319 bug callout):
 * `defaultFeatureLabel` from feature-extractor appends "+"/"-" alongside the
 * "positively/negatively charged" prose. Stripped here so the verbal charge
 * stands alone.
 */
const TRAILING_CHARGE_SYMBOL_RE = / *[+-]+( *)(?= of |$)/g;

/**
 * Strip the leading "positively/negatively charged " prefix if present.
 * Returns the trimmed name; idempotent.
 */
function stripChargePrefix(name: string): string {
  return name.replace(CHARGE_PREFIX_RE, '');
}

/**
 * PIPH-319 prose normalization. Strips the trailing charge symbol that
 * `defaultFeatureLabel` emits alongside the verbal "positively/negatively
 * charged" prefix, and (when `lowercase`) lowercases the leading element label.
 *
 * Only the leading element name + optional positional suffix is lowercased
 * ("Carbon-2" → "carbon-2", "Oxygen" → "oxygen"). Feature names that
 * follow (e.g. "methyl group-A of 4-carbon chain", "HBr", "H₂O") preserve
 * their original case — the disambiguating letter suffix on "methyl group-A"
 * stays uppercase, and chemical formulas keep their case.
 */
const LEADING_ELEMENT_RE = /^([A-Z][a-z]+(?:-\d+)?)/;

function normalizeProse(name: string, lowercase: boolean): string {
  // Strip the bare "+"/"-" charge marker that sits before " of " or at end.
  const out = name.replace(TRAILING_CHARGE_SYMBOL_RE, (_, trailingSpace: string) => trailingSpace);

  if (!lowercase) return out;

  // The verbal charge prefix may sit before the leading element label;
  // lowercase the element label after that prefix.
  const chargeMatch = out.match(CHARGE_PREFIX_RE);
  const prefix = chargeMatch ? chargeMatch[0] : '';
  const rest = out.slice(prefix.length);

  const lowered = rest.replace(LEADING_ELEMENT_RE, m => m.toLowerCase());
  return prefix + lowered;
}

/**
 * Resolve an atom index to its rendered name, applying PIPH-319 brevity:
 *
 *  - Rule 1: drop "of {feature}" when feature name (case-insensitive) equals
 *    the element name (e.g. "oxygen of Oxygen" → "oxygen").
 *  - Rule 2: on second mention of the same atom, drop both the leading
 *    charge prefix and the " of {feature}" suffix.
 *  - Rule 5 (assisted by caller): when `sharedFeatureName` is supplied,
 *    strip the trailing " of {sharedFeatureName}" — the caller appends it
 *    once to the joined output.
 *
 * Atom-N leak fix: when the atom isn't in `serialized.atoms`, resolve the
 * element symbol via the workspace and emit the lowercased element name.
 */
export function renderAtomName(
  ctx: BrevityCtx,
  atomIndex: number | undefined,
  opts: { sharedFeatureName?: string } = {},
): string {
  if (atomIndex === undefined) return 'atom';

  const atom = ctx.serialized.atoms.find(a => a.index === atomIndex);
  if (!atom) {
    // PIPH-319: explicit-H fallback. The serializer folds explicit Hs into
    // their parent's hydrogenCount, so a bond touching a hydrogen index
    // can land outside serialized.atoms. Resolve via the workspace symbol
    // map and emit the lowercased element name ("hydrogen") instead of
    // the placeholder "Atom-{n}".
    const symbol = workspaceSymbolForIndex(ctx, atomIndex);
    if (!symbol) return 'atom';
    return (getElementName(symbol) ?? symbol).toLowerCase();
  }

  const isRepeat = ctx.mentioned.has(atomIndex);
  ctx.mentioned.add(atomIndex);

  // PIPH-319 prose normalization: lowercase the atom label and strip the
  // trailing "+"/"-" charge symbol that leaks alongside the verbal prefix.
  const name = normalizeProse(getAtomNameWithChargeContext(atomIndex, ctx.serialized), ctx.lowercase);

  // Rule 2: drop charge + feature suffix on repeat mention.
  if (isRepeat) {
    const [bare] = splitFeatureSuffix(stripChargePrefix(name));
    return bare;
  }

  // Split off the " of {feature}" suffix for Rules 1 and 5 inspection.
  const [bare, featureName] = splitFeatureSuffix(name);
  if (!featureName) return name;

  // Rule 1: drop suffix when feature name matches the element name.
  // Compare against the *bare* atom term with any charge prefix stripped,
  // so "negatively charged oxygen of Oxygen" → "negatively charged oxygen".
  const elementBare = stripChargePrefix(bare);
  if (featureName.toLowerCase() === elementBare.toLowerCase()) {
    return bare;
  }

  // Rule 5: caller is rendering a shared-feature bond phrase and wants the
  // suffix elided so they can append it once at the end.
  if (opts.sharedFeatureName && featureName === opts.sharedFeatureName) {
    return bare;
  }

  // PIPH-319 Rule 2 (feature extension): the listener already heard this
  // feature in an earlier clause of the same description. Drop the suffix.
  if (ctx.mentionedFeatures.has(featureName)) {
    return bare;
  }

  ctx.mentionedFeatures.add(featureName);
  return name;
}

/**
 * Resolve the feature suffix that an atom's rendered name would carry.
 * Returns `undefined` for atoms that don't carry a feature suffix
 * (singleton-feature atoms post-Rule-1, hydrogens not in serialized.atoms,
 * etc.). Used by Rule 5 to decide whether two atoms share a feature.
 *
 * NOTE: does not consume a mention slot.
 */
export function peekFeatureName(ctx: BrevityCtx, atomIndex: number | undefined): string | undefined {
  if (atomIndex === undefined) return undefined;
  const atom = ctx.serialized.atoms.find(a => a.index === atomIndex);
  if (!atom) return undefined;
  const name = normalizeProse(getAtomNameWithChargeContext(atomIndex, ctx.serialized), ctx.lowercase);
  const [bare, featureName] = splitFeatureSuffix(name);
  if (!featureName) return undefined;
  // Rule 1 collapses these — they don't count as a "feature" for sharing.
  const elementBare = stripChargePrefix(bare);
  if (featureName.toLowerCase() === elementBare.toLowerCase()) return undefined;
  return featureName;
}

/**
 * Describe a bond's two atom endpoints, with Rule 5 applied: if both atoms
 * share a feature, render bare names and append " of {feature}" once.
 *
 * @param atom1Index  First atom index (in render order — typically the anchor).
 * @param atom2Index  Second atom index.
 */
export function renderBondAtoms(
  ctx: BrevityCtx,
  atom1Index: number | undefined,
  atom2Index: number | undefined,
): string {
  // Rule 5: detect a shared feature *before* either atom is marked mentioned,
  // so the decision is independent of mention order.
  const f1 = peekFeatureName(ctx, atom1Index);
  const f2 = peekFeatureName(ctx, atom2Index);
  const sharedFeatureName = f1 && f1 === f2 ? f1 : undefined;

  // Rule 2 (feature extension): if the shared feature has already been
  // mentioned earlier in this description, the suffix is dropped entirely.
  const featureAlreadyMentioned =
    sharedFeatureName !== undefined && ctx.mentionedFeatures.has(sharedFeatureName);

  const name1 = renderAtomName(ctx, atom1Index, { sharedFeatureName });
  const name2 = renderAtomName(ctx, atom2Index, { sharedFeatureName });

  if (sharedFeatureName && !featureAlreadyMentioned) {
    ctx.mentionedFeatures.add(sharedFeatureName);
    return `${name1} and ${name2} of ${sharedFeatureName}`;
  }
  return `${name1} and ${name2}`;
}
